import { Icon } from "@/components/ui/Icon";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "@heroui/react";
import type { ReservationUi } from "../reservationFormUi";

type SubmitBarProps = {
  t: ReservationUi["step1"];
  onEstimatePrice: () => void;
  onSubmit: () => void;
  isReserving: boolean;
  isCalculatingPrice: boolean;
  estimatedPrice: number | null;
  priceError: string | null;
  estimateActionError: string | null;
  reserveActionError: string | null;
  // Modal « passer en aller simple »
  isModalOpen: boolean;
  onModalClose: () => void;
  onSwitchToOneWay: () => void;
};

/**
 * Barre d'actions de l'étape 1 : bouton « Estimer le prix », affichage du prix
 * estimé / erreurs, bouton « Réserver » (avec garde anti-double-soumission via
 * `isReserving`), et la modale de bascule en aller simple. Bloc présentationnel
 * extrait verbatim de ReservationForm (aucun changement de comportement).
 */
export const SubmitBar = ({
  t,
  onEstimatePrice,
  onSubmit,
  isReserving,
  isCalculatingPrice,
  estimatedPrice,
  priceError,
  estimateActionError,
  reserveActionError,
  isModalOpen,
  onModalClose,
  onSwitchToOneWay,
}: SubmitBarProps) => {
  return (
    <>
      {/* Bouton de soumission */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-end">
        <button
          type="button"
          onClick={onEstimatePrice}
          disabled={isCalculatingPrice}
          className="flex items-center justify-center gap-2 rounded-lg bg-gold-100 px-6 py-3 font-medium text-gold-800 transition-colors hover:bg-gold-200"
        >
          {isCalculatingPrice ? (
            <Icon icon="ph:spinner" className="h-5 w-5 animate-spin" />
          ) : (
            <Icon icon="ph:calculator" className="h-5 w-5" />
          )}
          <span>{t.estimatePrice}</span>
        </button>

        {estimatedPrice !== null && !priceError && (
          <div className="font-syne flex items-center justify-center gap-2 text-lg font-bold text-black">
            <Icon icon="ph:money" className="h-5 w-5" />
            <span>{estimatedPrice}€</span>
          </div>
        )}

        {priceError && !estimateActionError && (
          <div className="text-center text-sm text-red-500">{priceError}</div>
        )}

        {estimateActionError && (
          <div className="w-full text-center text-sm text-red-500 sm:w-auto">
            {estimateActionError}
          </div>
        )}

        <button
          type="button"
          onClick={onSubmit}
          disabled={isReserving || isCalculatingPrice}
          className="flex items-center justify-center gap-2 rounded-lg bg-primary px-8 py-3 font-medium text-black transition-colors hover:bg-gold-600 hover:text-black disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isReserving ? (
            <Icon icon="ph:spinner" className="h-5 w-5 animate-spin" />
          ) : (
            <Icon icon="ph:calendar-check" className="h-5 w-5" />
          )}
          <span>{t.reserve}</span>
        </button>
      </div>

      {reserveActionError && (
        <div className="mt-3 text-end text-sm text-red-500">
          {reserveActionError}
        </div>
      )}

      {/* Modal de confirmation pour passer en aller simple */}
      <Modal
        isOpen={isModalOpen}
        onClose={onModalClose}
        className="bg-secondary"
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1 bg-primary">
            {t.modalTitle}
          </ModalHeader>
          <ModalBody>
            <p className="text-sm text-gold-800/80">{t.modalBody}</p>
          </ModalBody>
          <ModalFooter>
            <Button color="danger" variant="light" onPress={onModalClose}>
              {t.cancel}
            </Button>
            <Button color="primary" onPress={onSwitchToOneWay}>
              {t.continueOneWay}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
